// Single-source-of-truth content for SpaNearMeBhubaneswar.
// Business facts for Zorba International Spa come from the official spa menu
// (verified Oct 2026). Nothing is fabricated — if a field isn't in the menu,
// it stays null and the UI will show a transparent "to be confirmed" placeholder.

export const BRAND = {
  name: "Spa Near Me Bhubaneswar",
  short: "SpaNearMeBhubaneswar",
  tagline: "A calmer way to find massage and wellness in Bhubaneswar",
  description:
    "Discover professional massage, spa and wellness experiences across Bhubaneswar — with clear service information, honest guides and a simple way to enquire.",
  city: "Bhubaneswar",
  state: "Odisha",
  country: "India",
  url: "https://spanearmebhubaneswar.com",
  email: "hello@spanearmebhubaneswar.com",
};

// Verified from the Zorba International Spa menu (Oct 2026).
export const ZORBA = {
  name: "Zorba International Spa",
  disclosure: "Featured spa partner",
  tagline: "A world of wellness wrapped in luxury",
  area: "Chandrasekharpur",
  city: "Bhubaneswar",
  state: "Odisha",
  country: "India",
  postalCode: "751016",
  address: "3rd Floor, Stalwart Xanadu, Chandrasekharpur, Bhubaneswar, Odisha 751016",
  phone: "+918522816969",
  phoneDisplay: "+91 85228 16969",
  whatsapp: "918522816969",
  hours: null, // not published in the official menu
  mapUrl:
    "https://www.google.com/maps/search/?api=1&query=Zorba+International+Spa+Stalwart+Xanadu+Chandrasekharpur+Bhubaneswar",
  priceRange: "₹₹ — ₹₹₹",
  heroImage: "/images/zorba/reception.webp",
  galleryImages: [
    "/images/zorba/reception.webp",
    "/images/zorba/couples-suite.webp",
    "/images/zorba/treatment-room.webp",
    "/images/zorba/corridor.webp",
    "/images/zorba/vanity.webp",
  ],
  services: [
    "Zorba Signature Therapy",
    "Four Hands Massage",
    "Couple Massage",
    "Thai Massage",
    "Aroma Therapy",
    "Swedish Therapy",
    "Balinese Therapy",
    "Deep Tissue Massage",
    "Sport Massage",
    "Candle Massage",
    "Potli Massage",
    "Hot Oil Therapy",
    "Body Scrub Therapy",
    "Jacuzzi Therapy",
    "Lomi Lomi Massage",
    "Wooden Deep Tissue Roller Massage",
    "Bamboo Massage",
    "Hot Stone Massage",
  ],
  facilities: [
    "Private treatment rooms",
    "Couples suite",
    "Jacuzzi",
    "Candle-lit relaxation setting",
    "Shower and changing area",
    "Reception and waiting lounge",
  ],
};

const INR = (n) => `₹${n.toLocaleString("en-IN")}`;

export const WHATSAPP_DEFAULT_MESSAGE =
  `Hello Zorba International Spa, I would like to enquire about a wellness visit at your Chandrasekharpur, Bhubaneswar location.`;

export const whatsappLink = (message = WHATSAPP_DEFAULT_MESSAGE) => {
  const base = ZORBA.whatsapp ? `https://wa.me/${ZORBA.whatsapp}` : "https://wa.me/";
  return `${base}?text=${encodeURIComponent(message)}`;
};

// Signature package — the official "6-in-1 Wellness Experience".
export const SIGNATURE_PACKAGE = {
  slug: "signature",
  name: "Zorba Signature Therapy",
  kicker: "6-in-1 Wellness Experience",
  short: "A complete 90-minute journey designed to relax, refresh and restore.",
  image: "/images/zorba/signature.jpg",
  duration: ["90 min"],
  price: 6000,
  priceDisplay: INR(6000),
  priceOptions: [{ duration: "90 min", amount: 6000, display: INR(6000) }],
  inclusions: [
    "10-min foot massage to ease tired feet",
    "10-min aroma massage with soothing oils",
    "20-min deep tissue massage to release muscular tension",
    "20-min Thai massage for flexibility and body balance",
    "15-min head massage to ease stress",
    "15-min body scrub for smooth, refreshed skin",
  ],
  intro:
    "Zorba's 90-minute signature journey layers six considered rituals into a single wellness experience — a soft start at the feet, a calming aroma massage, deep-tissue release, gentle Thai stretching, a stress-easing head massage, and a finishing body scrub. It is the quickest way to understand what Zorba means by a world of wellness wrapped in luxury.",
};

// Service menu — verified prices from the Zorba International Spa menu.
export const SERVICES = [
  {
    slug: "four-hands-massage",
    name: "Four Hands Massage",
    kicker: "Synchronised, luxurious",
    short: "Two skilled therapists moving in harmony for a deeply immersive wellness session.",
    image: "/images/zorba/four-hands.jpg",
    duration: ["60 min", "90 min", "120 min"],
    priceOptions: [
      { duration: "60 min", amount: 5000, display: INR(5000) },
      { duration: "90 min", amount: 6000, display: INR(6000) },
      { duration: "120 min", amount: 7000, display: INR(7000) },
    ],
    intro:
      "Two therapists work in perfect synchrony, delivering mirrored strokes across the body. The effect is more than the sum of two hands — the mind struggles to track two sets of movements at once, and quickly surrenders. It's one of the most immersive ways to spend an hour at Zorba.",
    suitsWho: [
      "Guests who want a maximal, luxurious wellness experience",
      "Anyone who has tried solo massage and is ready for something elevated",
      "A reset after a long work stretch or demanding travel",
    ],
    experience: [
      "A short consultation to understand pressure preference",
      "Two therapists working in sync on mirrored strokes",
      "Warmed oil, considered pressure, and a slow pace",
      "A quiet rest at the end",
    ],
    before: [
      "Hydrate well through the day",
      "Avoid a heavy meal in the hour before the session",
      "Share any injuries or sensitivities in advance",
    ],
    aftercare: [
      "Keep drinking water",
      "Allow a quiet hour after the session if possible",
      "Avoid heavy training the same evening",
    ],
    faqs: [
      {
        q: "Is a four hands massage firm?",
        a: "The pressure can be adjusted. Most guests find a medium pressure is the right balance — it allows you to notice the synchronised movement without it feeling intense.",
      },
      {
        q: "How is it different from a regular massage?",
        a: "The second therapist means the whole body is engaged simultaneously — the mind lets go faster because it can't anticipate strokes.",
      },
    ],
    related: ["signature", "couple-massage", "deep-tissue-massage"],
  },
  {
    slug: "couple-massage",
    name: "Couple Massage",
    kicker: "A shared ritual",
    short: "A personalised, side-by-side massage for two — choose any style for each person.",
    image: "/images/zorba/couples-suite.webp",
    duration: ["60 min", "90 min", "120 min"],
    priceOptions: [
      { duration: "60 min", amount: 6500, display: INR(6500) },
      { duration: "90 min", amount: 7500, display: INR(7500) },
      { duration: "120 min", amount: 12500, display: INR(12500) },
    ],
    styleOptions: ["Aroma", "Swedish", "Balinese", "Deep Tissue", "Candle"],
    intro:
      "A couple's experience at Zorba is as much about shared time as it is about the massage. The couples suite holds two tables, two therapists, and a quiet atmosphere designed to help the two of you simply be here. You can even pick different styles — one Swedish, one Deep Tissue, or any mix from Aroma, Balinese and Candle.",
    suitsWho: [
      "Couples planning a quiet date in Bhubaneswar",
      "Anniversaries, birthdays and small celebrations",
      "Partners travelling through the city together",
    ],
    experience: [
      "A short consultation on preferred style and pressure for each person",
      "Side-by-side tables in a private couples suite",
      "Two therapists working in parallel",
      "A quiet space to sit together afterwards",
    ],
    before: [
      "Mention any allergies, pregnancy or injuries in advance",
      "Confirm the couples suite is available for the time you have in mind",
      "Arrive 10 minutes early to settle in together",
    ],
    aftercare: [
      "A light meal nearby fits the mood",
      "Keep the evening quiet",
      "Hydrate well",
    ],
    faqs: [
      {
        q: "Can we pick different services?",
        a: "Yes — each partner can pick a different style, pressure and duration from Aroma, Swedish, Balinese, Deep Tissue or Candle.",
      },
      {
        q: "Is the couples suite always available?",
        a: "It is a limited-capacity room, so we recommend sending a quick WhatsApp enquiry to confirm your preferred time.",
      },
    ],
    related: ["signature", "aroma-massage", "candle-massage"],
  },
  {
    slug: "thai-massage",
    name: "Thai Massage",
    kicker: "Assisted stretching",
    short: "Rhythmic pressure, acupressure and assisted stretching to restore flexibility.",
    image: "/images/zorba/thai.jpg",
    duration: ["60 min", "90 min", "120 min"],
    priceOptions: [
      { duration: "60 min", amount: 2500, display: INR(2500) },
      { duration: "90 min", amount: 3400, display: INR(3400) },
      { duration: "120 min", amount: 4300, display: INR(4300) },
    ],
    intro:
      "The traditional art of Thai massage combines rhythmic pressure, acupressure along energy lines, and gentle assisted stretching. It feels more active than an oil massage — you remain in comfortable clothing while the therapist guides your body through a sequence that leaves you feeling noticeably more open and lighter on your feet.",
    suitsWho: [
      "People who sit a lot and want to feel mobile again",
      "Guests who prefer no oil",
      "Anyone curious about traditional bodywork",
    ],
    experience: [
      "Session takes place in loose, comfortable clothing on a mat",
      "A sequence of gentle stretches and compressions",
      "Palm, thumb and forearm pressure along specific points",
      "A quiet moment to let your body settle",
    ],
    before: [
      "Wear or arrive in clothes you can stretch in",
      "Avoid eating heavily in the hour before",
      "Mention any knee, hip or shoulder issues in advance",
    ],
    aftercare: [
      "Keep moving gently for the next hour",
      "Drink water",
      "Avoid strenuous training the same evening",
    ],
    faqs: [
      {
        q: "Do I need to be flexible?",
        a: "Not at all. The therapist works entirely within your comfortable range.",
      },
      {
        q: "Is Thai massage painful?",
        a: "There may be moments of firm pressure, but it should always stay within your comfort limit.",
      },
    ],
    related: ["balinese-massage", "deep-tissue-massage", "signature"],
  },
  {
    slug: "aroma-massage",
    name: "Aroma Therapy",
    kicker: "Sensory ritual",
    short: "A luxurious full-body massage with carefully selected essential oils.",
    image: "/images/zorba/aroma.jpg",
    duration: ["60 min", "90 min", "120 min"],
    priceOptions: [
      { duration: "60 min", amount: 2800, display: INR(2800) },
      { duration: "90 min", amount: 3600, display: INR(3600) },
      { duration: "120 min", amount: 4500, display: INR(4500) },
    ],
    intro:
      "Aroma therapy at Zorba is a full-body massage built around slow, flowing strokes and considered essential oil blends. Lavender and chamomile tilt the room toward calm. Eucalyptus and sweet orange tilt it toward clarity. Your therapist will suggest a blend that fits the mood you're arriving with.",
    suitsWho: [
      "Guests who find it hard to switch off",
      "People who enjoy sensory experiences",
      "Those recovering from a stressful or sleepless stretch",
    ],
    experience: [
      "A short conversation about scent and mood",
      "Oils are selected and warmed before application",
      "Gentle, rhythmic strokes to help the mind settle",
      "A quiet rest period at the end",
    ],
    before: [
      "Mention any scent sensitivities or allergies",
      "Avoid strong perfumes before the session",
      "Arrive a few minutes early to decompress",
    ],
    aftercare: [
      "Keep the evening gentle and screen-light",
      "Drink water through the rest of the day",
      "Delay a shower if you'd like the oils to continue working on the skin",
    ],
    faqs: [
      {
        q: "Can aroma therapy help with sleep?",
        a: "Many guests find the ritual helps them wind down more easily, especially on a quiet evening.",
      },
      {
        q: "Which oils are available?",
        a: "Common choices include lavender, chamomile, eucalyptus, sweet orange and sandalwood. Your therapist can guide the choice based on how you'd like to feel afterwards.",
      },
    ],
    related: ["swedish-massage", "candle-massage", "signature"],
  },
  {
    slug: "swedish-massage",
    name: "Swedish Therapy",
    kicker: "Classic relaxation",
    short: "Long, flowing strokes with light-to-medium pressure for complete relaxation.",
    image: "/images/zorba/swedish.jpg",
    duration: ["60 min", "90 min", "120 min"],
    priceOptions: [
      { duration: "60 min", amount: 3000, display: INR(3000) },
      { duration: "90 min", amount: 4000, display: INR(4000) },
      { duration: "120 min", amount: 4900, display: INR(4900) },
    ],
    intro:
      "Swedish therapy is the time-tested starting point of modern wellness massage — long gliding strokes, light kneading, circular movements and warmed oil. The pressure stays gentle to medium, which makes it a reliable choice for first-time guests and anyone who simply wants to slow the week down.",
    suitsWho: [
      "First-time spa visitors",
      "Working professionals unwinding after a long week",
      "Travellers arriving into Bhubaneswar",
      "Anyone preferring a lighter, calming pressure",
    ],
    experience: [
      "Warm towel welcome and a quick comfort check",
      "Long, flowing strokes with warmed oil",
      "Gentle kneading on common tension points",
      "A quiet rest period before you step back out",
    ],
    before: [
      "Avoid a heavy meal in the 60 minutes before",
      "Arrive 10 minutes early so you can settle in",
      "Share any injuries, allergies or sensitivities",
    ],
    aftercare: [
      "Drink water through the rest of the day",
      "Avoid strenuous exercise for a few hours",
      "Give yourself a quiet evening if possible",
    ],
    faqs: [
      {
        q: "Is Swedish good for first-time guests?",
        a: "Yes — the pressure is lighter and the pace unhurried, which makes it a comfortable starting point.",
      },
      {
        q: "How long should a Swedish session be?",
        a: "60 minutes covers the essentials. 90 minutes allows more time on shoulders, lower back and legs.",
      },
    ],
    related: ["aroma-massage", "deep-tissue-massage", "signature"],
  },
  {
    slug: "balinese-massage",
    name: "Balinese Therapy",
    kicker: "Flowing & grounding",
    short: "A traditional Indonesian blend of gentle stretching, acupressure and rhythmic strokes.",
    image: "/images/zorba/balinese.jpg",
    duration: ["60 min", "90 min", "120 min"],
    priceOptions: [
      { duration: "60 min", amount: 3500, display: INR(3500) },
      { duration: "90 min", amount: 4500, display: INR(4500) },
      { duration: "120 min", amount: 4900, display: INR(4900) },
    ],
    intro:
      "Balinese therapy weaves together gentle stretches, acupressure, skin rolling and long flowing strokes with aromatic oil. The pace is medium — firmer than Swedish, less sustained than deep tissue — and it leaves many guests feeling both calmer in the mind and lighter on the feet.",
    suitsWho: [
      "Guests who enjoy a mix of pressure and stretch",
      "Those who sit for long hours and feel generally stiff",
      "Travellers wanting a signature wellness experience",
    ],
    experience: [
      "Gentle warm-up strokes across back, shoulders and legs",
      "Thumb and palm acupressure on tense points",
      "Slow, assisted stretches to open the hips and shoulders",
      "A flowing finish with warmed oil",
    ],
    before: [
      "Share any joint concerns or injuries beforehand",
      "Hydrate well on the day",
      "Avoid tight clothing for the ride home",
    ],
    aftercare: [
      "Walk a little before sitting back down",
      "Keep up water intake through the evening",
      "A short, light meal afterwards fits best",
    ],
    faqs: [
      {
        q: "Is Balinese firm?",
        a: "The pressure can be adjusted. It's typically medium — firmer than Swedish, less sustained than deep tissue.",
      },
      {
        q: "Is it a full-body experience?",
        a: "Yes. The flow usually covers back, legs, arms, shoulders and sometimes head.",
      },
    ],
    related: ["thai-massage", "aroma-massage", "deep-tissue-massage"],
  },
  {
    slug: "deep-tissue-massage",
    name: "Deep Tissue Massage",
    kicker: "Focused relief",
    short: "Slow, firm pressure to reach the deeper muscle layers and release chronic tension.",
    image: "/images/zorba/deep-tissue.jpg",
    duration: ["60 min", "90 min", "120 min"],
    priceOptions: [
      { duration: "60 min", amount: 3500, display: INR(3500) },
      { duration: "90 min", amount: 4500, display: INR(4500) },
      { duration: "120 min", amount: 5500, display: INR(5500) },
    ],
    intro:
      "Deep tissue massage uses slower strokes and sustained pressure to reach the deeper layers of muscle and connective tissue. It's a thoughtful pick when you've been carrying tightness in the shoulders, back or legs for a while — not something you book on a whim.",
    suitsWho: [
      "Desk workers with chronic shoulder or neck tightness",
      "People who exercise regularly and feel muscle fatigue",
      "Anyone recovering soreness from long travel",
      "Guests who prefer firmer pressure",
    ],
    experience: [
      "A short consultation to identify focus areas",
      "Slower strokes with firmer pressure on those areas",
      "Breath cues from your therapist to help you relax into pressure",
      "A gentle cooldown with lighter strokes before you finish",
    ],
    before: [
      "Hydrate well in the hours leading up to the session",
      "Avoid booking immediately after an intense workout",
      "Mention any injuries, pregnancy or medical conditions",
    ],
    aftercare: [
      "Mild soreness over the next 24 hours is normal",
      "Keep drinking water and consider a warm shower in the evening",
      "Avoid heavy training for at least a day",
    ],
    faqs: [
      {
        q: "How is deep tissue different from Swedish?",
        a: "Swedish is lighter and primarily relaxing. Deep tissue is slower and firmer, aimed at specific areas of tension.",
      },
      {
        q: "Will it hurt?",
        a: "The pressure is firm but should stay within your comfort limit. Always speak up if the pressure feels too strong.",
      },
    ],
    related: ["sport-massage", "wooden-roller-massage", "signature"],
  },
  {
    slug: "sport-massage",
    name: "Sport Massage",
    kicker: "Active body recovery",
    short: "Focused deep-pressure techniques for tired, tight and overworked muscles.",
    image: "/images/zorba/sport.jpg",
    duration: ["60 min", "90 min", "120 min"],
    priceOptions: [
      { duration: "60 min", amount: 4000, display: INR(4000) },
      { duration: "90 min", amount: 5000, display: INR(5000) },
      { duration: "120 min", amount: 6000, display: INR(6000) },
    ],
    intro:
      "Sport massage targets tired, tight and overworked muscles with focused deep-pressure techniques. It helps release accumulated tension, support flexibility and move recovery along — well suited to anyone training consistently, or in the thick of a demanding physical stretch.",
    suitsWho: [
      "Runners, cyclists and gym-goers",
      "Anyone recovering from a demanding week",
      "Guests who prefer firmer, focused work on specific muscle groups",
    ],
    experience: [
      "A quick chat about recent training load and problem areas",
      "Deep-pressure strokes on the identified muscle groups",
      "Short stretches to assist range of motion",
      "A lighter closing section before you finish",
    ],
    before: [
      "Hydrate well before and after",
      "Avoid a heavy workout within an hour of the session",
      "Share any recent injuries or soreness",
    ],
    aftercare: [
      "Expect mild tenderness for 12 to 24 hours",
      "Keep training light the next day",
      "Hydrate well",
    ],
    faqs: [
      {
        q: "Can I book this before a race or event?",
        a: "A lighter session 48 to 72 hours before an event tends to work better than a deep session the day before. Share your event date and your therapist will adjust pressure accordingly.",
      },
    ],
    related: ["deep-tissue-massage", "wooden-roller-massage", "signature"],
  },
  {
    slug: "candle-massage",
    name: "Candle Massage",
    kicker: "Warm and nourishing",
    short: "A warm, nourishing massage using melted massage candles with skin-loving butters.",
    image: "/images/zorba/candle.jpg",
    duration: ["60 min", "90 min"],
    priceOptions: [
      { duration: "60 min", amount: 3800, display: INR(3800) },
      { duration: "90 min", amount: 4800, display: INR(4800) },
    ],
    intro:
      "Specially formulated massage candles are warmed before you arrive. The oil pools at skin-safe warmth and is then poured gently onto the body as part of a slow, meditative session. The warmth helps the body let go more quickly than a cold-oil massage, and the skin feels soft and hydrated long after.",
    suitsWho: [
      "Guests who want extra warmth, especially in the cooler evenings",
      "People looking for a slow, immersive ritual",
      "Those who enjoy sensory wellness experiences",
    ],
    experience: [
      "The candle is warmed before your session",
      "Warm oil is slowly poured and worked across back, legs and arms",
      "Long, meditative strokes are the heart of the session",
      "A quiet rest completes the ritual",
    ],
    before: [
      "Mention sensitive skin or burn history",
      "Avoid new skincare the same day",
    ],
    aftercare: [
      "The oil continues to nourish the skin — delay a shower if you can",
      "Keep hydrated",
      "Keep the evening calm",
    ],
    faqs: [
      {
        q: "Is the wax safe for skin?",
        a: "Dedicated massage candles use skin-safe ingredients that melt at a low, body-safe temperature. Your therapist checks the temperature before each use.",
      },
    ],
    related: ["aroma-massage", "hot-oil-therapy", "signature"],
  },
  {
    slug: "potli-massage",
    name: "Potli Massage",
    kicker: "Ayurvedic herbal poultices",
    short: "Warm herbal poultices work with soothing strokes to ease stiffness and tension.",
    image: "/images/zorba/potli.jpg",
    duration: ["60 min", "90 min"],
    priceOptions: [
      { duration: "60 min", amount: 3500, display: INR(3500) },
      { duration: "90 min", amount: 4500, display: INR(4500) },
    ],
    intro:
      "A traditional Ayurvedic therapy — small cloth pouches (potlis) are filled with therapeutic herbs, warmed, and gently pressed and glided across the body alongside soothing massage techniques. The warmth and the herbs together ease muscle stiffness, support circulation and offer a quietly grounding wellness ritual.",
    suitsWho: [
      "Guests curious about Ayurveda-inspired wellness",
      "People who feel generally stiff or sore after travel",
      "Anyone looking for a slower, more grounded session",
    ],
    experience: [
      "Potlis are warmed before your session",
      "Gentle gliding and pressing techniques alongside massage strokes",
      "The warmth is maintained throughout the session",
      "A quiet rest at the end",
    ],
    before: [
      "Mention sensitive skin or any herbal allergies",
      "Hydrate well",
    ],
    aftercare: [
      "Let the warmth sit on the skin — delay a shower if possible",
      "Keep the evening quiet",
      "Hydrate",
    ],
    faqs: [
      {
        q: "What herbs are used?",
        a: "A traditional Ayurvedic blend is used. Mention any known allergies to your therapist, who can adjust the mix if needed.",
      },
    ],
    related: ["hot-oil-therapy", "candle-massage", "signature"],
  },
  {
    slug: "hot-oil-therapy",
    name: "Hot Oil Therapy",
    kicker: "Nourishing warmth",
    short: "Warm aromatic oils melt muscle tension and nourish the skin.",
    image: "/images/zorba/hot-oil.jpg",
    duration: ["60 min", "90 min", "120 min"],
    priceOptions: [
      { duration: "60 min", amount: 3200, display: INR(3200) },
      { duration: "90 min", amount: 4200, display: INR(4200) },
      { duration: "120 min", amount: 5000, display: INR(5000) },
    ],
    intro:
      "Warm aromatic oil is poured and slowly worked across the body with gentle massage techniques. The warmth helps muscles release tension more quickly than a cold-oil massage, while the oil leaves the skin feeling nourished and soft. A good, dependable choice for a longer, calmer session.",
    suitsWho: [
      "Guests looking for a gentle but immersive wellness session",
      "People who enjoy warmth in their massage",
      "Those with drier skin wanting a nourishing finish",
    ],
    experience: [
      "The oil is warmed before you arrive",
      "A slow, flowing massage across the whole body",
      "Steady warmth throughout the session",
      "A quiet rest at the end",
    ],
    before: [
      "Mention sensitive skin or scent sensitivities",
      "Avoid new skincare the same day",
    ],
    aftercare: [
      "Delay a shower if you'd like the oil to continue nourishing the skin",
      "Hydrate well",
    ],
    faqs: [
      {
        q: "How is hot oil different from aroma therapy?",
        a: "Hot oil focuses on warmth and the physical release of tension. Aroma therapy leans harder on scent-driven mood work.",
      },
    ],
    related: ["aroma-massage", "candle-massage", "swedish-massage"],
  },
  {
    slug: "body-scrub-therapy",
    name: "Body Scrub Therapy",
    kicker: "Full-body exfoliation",
    short: "A rejuvenating full-body exfoliation that reveals smoother, softer skin.",
    image: "/images/zorba/body-scrub.jpg",
    duration: ["60 min", "90 min"],
    priceOptions: [
      { duration: "60 min", amount: 3500, display: INR(3500) },
      { duration: "90 min", amount: 4500, display: INR(4500) },
    ],
    intro:
      "A premium natural scrub is used to gently exfoliate the whole body — removing dead skin cells, improving skin texture, and preparing the skin to absorb oils and moisture more effectively. It pairs beautifully with a massage before or after, and leaves the skin visibly brighter.",
    suitsWho: [
      "Guests who feel their skin looks dull or feels dry",
      "Travellers after a long flight",
      "Anyone wanting a bright, polished-skin finish",
    ],
    experience: [
      "A warm shower before the scrub",
      "Gentle, considered application across the body",
      "A rinse and a short calming moment to finish",
    ],
    before: [
      "Avoid shaving on the same day",
      "Mention any skin sensitivities",
    ],
    aftercare: [
      "Moisturise through the next couple of days",
      "Avoid direct strong sunlight for a few hours",
    ],
    faqs: [
      {
        q: "Can I combine it with a massage?",
        a: "Yes — a scrub before a massage lets the oil absorb more deeply, and the whole experience feels more complete. Ask when enquiring.",
      },
    ],
    related: ["hot-oil-therapy", "signature", "aroma-massage"],
  },
  {
    slug: "jacuzzi-spa",
    name: "Jacuzzi Therapy",
    kicker: "Warm hydrotherapy",
    short: "Warm bubbling water and gentle jets melt tension and reset the body.",
    image: "/images/zorba/jacuzzi.jpg",
    duration: ["60 min", "90 min", "120 min"],
    priceOptions: [
      { duration: "60 min", amount: 5000, display: INR(5000) },
      { duration: "90 min", amount: 6000, display: INR(6000) },
      { duration: "120 min", amount: 7000, display: INR(7000) },
    ],
    intro:
      "A luxurious hydrotherapy soak where warm, moving water and gentle jets ease muscle tension, support circulation and leave the body feeling genuinely reset. Zorba's jacuzzi suite is a quiet, candle-lit space — a slow start or a soft finish to a longer wellness visit.",
    suitsWho: [
      "Guests pairing it with a massage",
      "Couples looking for a soft, immersive wellness break",
      "People who find warm water the quickest way to relax",
    ],
    experience: [
      "A warm shower before entering",
      "A longer soak with controlled jets of warm water",
      "A gentle shower afterwards",
      "Rest, hydrate, then continue your visit",
    ],
    before: [
      "Avoid alcohol before using the jacuzzi",
      "Skip if you have fever, open wounds or certain cardiac conditions — check with the spa",
    ],
    aftercare: [
      "Hydrate well",
      "Allow a few minutes to cool down before leaving",
    ],
    faqs: [
      {
        q: "Can I pair it with a massage?",
        a: "Yes — many guests enjoy a short soak before a massage. The warmth helps the body relax faster.",
      },
    ],
    related: ["signature", "hot-stone-massage", "couple-massage"],
  },
  {
    slug: "lomi-lomi-massage",
    name: "Lomi Lomi Massage",
    kicker: "Hawaiian wave-like strokes",
    short: "Long, flowing forearm strokes create a deeply relaxing, wave-like rhythm.",
    image: "/images/zorba/lomi-lomi.jpg",
    duration: ["60 min", "90 min", "120 min"],
    priceOptions: [
      { duration: "60 min", amount: 4000, display: INR(4000) },
      { duration: "90 min", amount: 4500, display: INR(4500) },
      { duration: "120 min", amount: 5000, display: INR(5000) },
    ],
    intro:
      "Hawaiian-inspired Lomi Lomi uses long, continuous strokes from hand and forearm to create a rhythmic, wave-like feel. The pacing is unusually smooth — the mind struggles to hold on to specific movements and tends to let go very quickly. A beautifully different experience from a strokes-and-kneading massage.",
    suitsWho: [
      "Guests who want something different from classic massage",
      "People who enjoy flow and rhythm in a session",
      "Those looking to properly switch off",
    ],
    experience: [
      "A soft start with warmed oil",
      "Long, flowing forearm strokes across the body",
      "A smooth, rhythmic pace throughout",
      "A quiet rest at the end",
    ],
    before: [
      "Hydrate well on the day",
      "Avoid heavy meals in the hour before",
    ],
    aftercare: [
      "Keep the evening gentle",
      "Hydrate",
    ],
    faqs: [
      {
        q: "What's the pressure like?",
        a: "Medium and even. The focus is on continuity of flow rather than on pressing into specific knots.",
      },
    ],
    related: ["aroma-massage", "swedish-massage", "signature"],
  },
  {
    slug: "wooden-roller-massage",
    name: "Wooden Deep Tissue Roller Massage",
    kicker: "Rolling release",
    short: "A specially crafted wooden roller releases tension with rhythmic rolling techniques.",
    image: "/images/zorba/wooden-roller.jpg",
    duration: ["60 min", "90 min"],
    priceOptions: [
      { duration: "60 min", amount: 4500, display: INR(4500) },
      { duration: "90 min", amount: 6500, display: INR(6500) },
    ],
    intro:
      "A dedicated wooden roller is used to work rhythmically across the body, reaching deeper layers of muscle and connective tissue. The sustained, even pressure is particularly effective at easing stiffness in the back, legs and shoulders — and it feels distinct from a traditional hands-only massage.",
    suitsWho: [
      "Guests with persistent back and leg tightness",
      "People who exercise regularly and want a deeper release",
      "Anyone curious about an alternative to classic deep tissue",
    ],
    experience: [
      "A warm-up with hands and oil",
      "Rhythmic rolling across identified areas",
      "A lighter closing section with hands",
      "A quiet rest at the end",
    ],
    before: [
      "Hydrate well",
      "Share recent injuries or soreness",
    ],
    aftercare: [
      "Expect mild tenderness for 12 to 24 hours",
      "Keep training light the next day",
      "Hydrate",
    ],
    faqs: [
      {
        q: "Is it painful?",
        a: "The pressure is firm but should always stay within your comfort limit. Speak up if a specific area feels sensitive.",
      },
    ],
    related: ["deep-tissue-massage", "bamboo-massage", "sport-massage"],
  },
  {
    slug: "bamboo-massage",
    name: "Bamboo Massage",
    kicker: "Rhythmic bamboo rolling",
    short: "Smooth bamboo sticks ease stiffness and restore circulation with rhythmic rolling.",
    image: "/images/zorba/bamboo.jpg",
    duration: ["60 min", "90 min"],
    priceOptions: [
      { duration: "60 min", amount: 4500, display: INR(4500) },
      { duration: "90 min", amount: 6500, display: INR(6500) },
    ],
    intro:
      "Smooth, warmed bamboo sticks are rolled rhythmically across the body to release muscle tension and support circulation. The sticks' varied shapes allow the therapist to work both broad strokes across the back and finer attention along the arms and legs — a distinctly different-feeling bodywork session.",
    suitsWho: [
      "Guests who've tried traditional massage and want something new",
      "People with general tightness through the back and legs",
      "Anyone drawn to natural-material wellness rituals",
    ],
    experience: [
      "A hands-on warm-up with oil",
      "Rhythmic bamboo rolling across the main muscle groups",
      "Finer attention on specific tense areas",
      "A quiet rest at the end",
    ],
    before: [
      "Hydrate well",
      "Mention sensitive skin or recent sunburn",
    ],
    aftercare: [
      "Keep up water intake",
      "Avoid intense training the same evening",
    ],
    faqs: [
      {
        q: "How does it compare to a wooden roller?",
        a: "The bamboo sticks are lighter and more varied in shape, so the texture of the pressure differs. Many guests find it a touch more gentle.",
      },
    ],
    related: ["wooden-roller-massage", "deep-tissue-massage", "signature"],
  },
  {
    slug: "hot-stone-massage",
    name: "Hot Stone Massage",
    kicker: "Heated stone warmth",
    short: "Smooth heated stones ease muscle tension and restore a sense of complete calm.",
    image: "/images/zorba/hot-stone.jpg",
    duration: ["60 min", "90 min"],
    priceOptions: [
      { duration: "60 min", amount: 4500, display: INR(4500) },
      { duration: "90 min", amount: 6500, display: INR(6500) },
    ],
    intro:
      "Smooth, polished stones are gently warmed and placed along the back, with others used to glide slowly across the body. The sustained warmth helps muscles release tension more deeply than cold hands alone can — and the overall feel is unusually calm and grounded.",
    suitsWho: [
      "Guests who find warmth the fastest route to relaxation",
      "People with mild chronic tightness in the back",
      "Those looking for a quietly immersive wellness experience",
    ],
    experience: [
      "Stones are warmed before you arrive",
      "A combination of placed stones and glided strokes",
      "Steady warmth throughout the session",
      "A quiet rest at the end",
    ],
    before: [
      "Mention heat sensitivities or cardiovascular conditions",
      "Hydrate well before the session",
    ],
    aftercare: [
      "Rehydrate thoroughly",
      "Keep the evening gentle",
    ],
    faqs: [
      {
        q: "Are the stones very hot?",
        a: "They are heated to a comfortable, body-safe warmth. Your therapist checks the temperature before contact and will adjust if anything feels too warm.",
      },
    ],
    related: ["jacuzzi-spa", "hot-oil-therapy", "signature"],
  },
];

export const SERVICE_SLUGS = SERVICES.map((s) => s.slug);
export const getService = (slug) => {
  if (slug === SIGNATURE_PACKAGE.slug) return SIGNATURE_PACKAGE;
  return SERVICES.find((s) => s.slug === slug);
};
export const ALL_SERVICES = [SIGNATURE_PACKAGE, ...SERVICES];

export const AREAS = [
  {
    slug: "chandrasekharpur",
    name: "Chandrasekharpur",
    kicker: "Where Zorba International Spa is based",
    headline: "Spa & massage in Chandrasekharpur",
    image: "/images/zorba/reception.webp",
    intro:
      "Chandrasekharpur is a calm, well-connected residential belt in the north of Bhubaneswar — and it's the home of Zorba International Spa, our featured wellness partner. Stalwart Xanadu, where the spa sits, is easy to reach from Patia, Sailashree Vihar, Niladri Vihar, Damana and the wider Infocity area.",
    paragraphs: [
      "For local residents, a short weekday evening session is the typical pattern. For working families, Saturday mornings often fit best — before the weekend's commitments begin. Zorba's couples suite and jacuzzi are especially popular for shared visits.",
      "Infocity and Infocity Square are also within easy reach, which makes this a reasonable mid-point if you're coming out of a work day in the IT belt.",
    ],
    nearby: ["Sailashree Vihar", "Niladri Vihar", "Damana", "Infocity", "Patia", "KIIT"],
    tips: [
      "Saturday morning slots are popular — book earlier in the week",
      "Ask about the couples suite if you're planning a date",
      "The jacuzzi pairs beautifully with a 60-minute massage",
    ],
    services: ["signature", "couple-massage", "jacuzzi-spa", "swedish-massage", "deep-tissue-massage"],
  },
  {
    slug: "patia",
    name: "Patia & KIIT",
    kicker: "Student-energy and new-city living",
    headline: "Spa & massage near Patia and KIIT",
    image: "/images/zorba/signature.jpg",
    intro:
      "Patia has grown around the KIIT University campus into one of Bhubaneswar's busiest neighbourhoods. It's full of young professionals, students and travellers — all of whom tend to want the same thing: an easy wellness break that doesn't take half a day.",
    paragraphs: [
      "Zorba International Spa is a short drive away in Chandrasekharpur — close enough that a 60-minute Swedish or aroma therapy fits comfortably between dinner plans or a weekend afternoon.",
      "When choosing a spa near Patia, look for clear service descriptions, transparent prices, and visible contact information. Zorba's menu publishes exact prices for every service — compare that against anyone else you're considering.",
    ],
    nearby: ["KIIT University", "Nandan Kanan Road", "Patia Station", "Chandrasekharpur"],
    tips: [
      "Evenings are busiest — plan ahead if you want a specific slot",
      "A 60-minute Swedish or aroma therapy is a popular first visit",
      "If you drive in, confirm parking before you arrive",
    ],
    services: ["swedish-massage", "aroma-massage", "deep-tissue-massage", "couple-massage"],
  },
  {
    slug: "jaydev-vihar",
    name: "Jaydev Vihar",
    kicker: "Central, well-connected, everyday wellness",
    headline: "Spa & massage near Jaydev Vihar",
    image: "/images/zorba/aroma.jpg",
    intro:
      "Jaydev Vihar sits at the centre of Bhubaneswar's commercial life — close to Nayapalli, Saheed Nagar and the eastern edge of the city. For most residents, a wellness visit here is about convenience: close to home or work, and easy to combine with an errand.",
    paragraphs: [
      "Zorba International Spa in Chandrasekharpur is a straightforward drive from Jaydev Vihar. A 60-minute session before dinner or on a Saturday afternoon is the most common pattern — the signature 90-minute 6-in-1 experience is a worthwhile upgrade when you have a bit more time.",
      "Jaydev Vihar's central position means Zorba is easy to reach from Saheed Nagar, Acharya Vihar, Nayapalli and Vani Vihar too.",
    ],
    nearby: ["Nayapalli", "Saheed Nagar", "Acharya Vihar", "Vani Vihar"],
    tips: [
      "Weekday evenings and Saturday afternoons book out first",
      "If parking matters, confirm it when enquiring",
      "Pair the massage with a quiet dinner nearby for a slower evening",
    ],
    services: ["signature", "swedish-massage", "aroma-massage", "couple-massage"],
  },
  {
    slug: "nayapalli",
    name: "Nayapalli",
    kicker: "IRC Village and the units",
    headline: "Spa & massage in Nayapalli",
    image: "/images/zorba/candle.jpg",
    intro:
      "Nayapalli and the surrounding Units 4–9 are quietly one of the most convenient parts of Bhubaneswar for a wellness break. The area is close to Jaydev Vihar, central enough for most residents, and well connected to Chandrasekharpur where Zorba International Spa is based.",
    paragraphs: [
      "A professional spa should feel calm from the moment you walk in. At Zorba, the reception area, treatment rooms and candle-lit couples suite set the tone. Treatment rooms, towels, oils, and the therapist's approach all matter more than lobby decoration.",
      "If you're visiting from IRC Village, Forest Park or Gajapati Nagar, Zorba is a short, direct drive north.",
    ],
    nearby: ["IRC Village", "Forest Park", "Unit 4", "Unit 6", "Unit 8", "Unit 9"],
    tips: [
      "Confirm your preferred time slot a day in advance when possible",
      "Try a 60-minute Swedish or aroma therapy if it's your first visit",
      "Ask about the Zorba Signature Therapy if you have 90 minutes to spare",
    ],
    services: ["swedish-massage", "aroma-massage", "signature", "couple-massage"],
  },
  {
    slug: "bhubaneswar-airport",
    name: "Airport area",
    kicker: "For travellers passing through Bhubaneswar",
    headline: "Spa & massage near Bhubaneswar Airport",
    image: "/images/zorba/hot-stone.jpg",
    intro:
      "If you're flying into or out of Biju Patnaik International Airport, a short spa visit is one of the simplest ways to reset. The airport connects easily to the main city, and Zorba International Spa in Chandrasekharpur is a reliable north-side option for a 60-minute session between flights or before heading home.",
    paragraphs: [
      "Travellers benefit most from gentle, well-paced sessions. Swedish, aroma therapy and hot oil therapy are easier on tired bodies than deep tissue after a long flight.",
      "If you're on a short layover, confirm total time commitment — travel in, massage, travel out — before you book. A WhatsApp enquiry is often the fastest way to do this.",
    ],
    nearby: ["Old Town", "Master Canteen", "Bapuji Nagar", "Airport Road"],
    tips: [
      "After a long flight, a 60-minute aroma or hot oil therapy usually feels best",
      "Allow 30+ minutes for travel either side",
      "Hydrate before and after — flights dehydrate the body",
    ],
    services: ["aroma-massage", "hot-oil-therapy", "swedish-massage"],
  },
];

export const AREA_SLUGS = AREAS.map((a) => a.slug);
export const getArea = (slug) => AREAS.find((a) => a.slug === slug);

AREAS.push(
  {
    slug: "kiit",
    name: "KIIT & Patia Station",
    kicker: "The academic belt that keeps growing",
    headline: "Spa & massage near KIIT University",
    image: "/images/zorba/signature.jpg",
    intro:
      "KIIT University anchors one of the fastest-growing parts of Bhubaneswar — a mix of hostels, PGs, cafes and new residential towers. A wellness visit here is usually a quick break between class, work and whatever comes next, and Zorba International Spa is a short northbound drive in Chandrasekharpur.",
    paragraphs: [
      "For students and young professionals around KIIT, Nandan Kanan Road and Patia Station, the practical pattern is simple: a 60-minute Swedish or aroma massage, often after dinner. The 90-minute signature journey is the usual pick for a weekend reset.",
      "If you drive in, confirm parking before you leave campus. Autos and app-cabs reach Chandrasekharpur comfortably in 10–12 minutes outside peak hours.",
    ],
    nearby: ["KIIT Square", "KIIT Road", "Nandan Kanan Road", "Patia Station"],
    tips: [
      "Weekday evenings fill up quickly — WhatsApp a day in advance",
      "The Monsoon Signature Ritual is well-priced for a longer visit",
      "Start with Swedish or aroma if it's your first time",
    ],
    services: ["swedish-massage", "aroma-massage", "signature", "deep-tissue-massage"],
  },
  {
    slug: "khandagiri",
    name: "Khandagiri & Baramunda",
    kicker: "Where the city opens westward",
    headline: "Spa & massage near Khandagiri",
    image: "/images/zorba/corridor.webp",
    intro:
      "Khandagiri and Baramunda sit along the western edge of Bhubaneswar — the quieter side of the city, close to the National Highway and a straightforward drive north toward Chandrasekharpur. Visitors heading in from Puri Road or Khurda often pass through here before continuing to the spa.",
    paragraphs: [
      "A wellness break here is best planned a little ahead of time. The drive to Chandrasekharpur takes around 25–30 minutes outside rush hour, and you'll want a service long enough to be worth the trip — a 90-minute aroma therapy or the signature journey.",
      "If you're combining a Khandagiri Caves day trip with a spa visit, a late-afternoon session before dinner is usually the smoothest sequence.",
    ],
    nearby: ["Khandagiri Caves", "Baramunda bus terminal", "Jagamara", "Bhubaneswar Railway Station"],
    tips: [
      "Allow 25–30 minutes drive to Chandrasekharpur each way",
      "A 90-minute service makes the drive feel worth it",
      "Call ahead on weekends — rooms book up by Thursday",
    ],
    services: ["aroma-massage", "signature", "deep-tissue-massage", "hot-stone-massage"],
  },
  {
    slug: "sahid-nagar",
    name: "Sahid Nagar",
    kicker: "The eastern commercial belt",
    headline: "Spa & massage near Sahid Nagar, Bhubaneswar",
    image: "/images/zorba/vanity.webp",
    intro:
      "Sahid Nagar is one of the city's older commercial neighbourhoods — restaurants, markets, offices and residential pockets, all close enough that a wellness visit can slip into an everyday weekday. Zorba International Spa in Chandrasekharpur is about a 20–25 minute drive north, depending on traffic.",
    paragraphs: [
      "For residents of Sahid Nagar, Rasulgarh and Mancheswar, a Friday evening or Saturday afternoon slot is the natural fit. Swedish, aroma, deep tissue and the signature journey are the most-booked services from this part of the city.",
      "If you work in Mancheswar Industrial Estate or around Kalpana Square, Zorba also makes sense after a long shift — the hot oil therapy is a popular late-evening wind-down.",
    ],
    nearby: ["Rasulgarh Square", "Mancheswar Industrial Estate", "Kalpana Square", "Vani Vihar"],
    tips: [
      "Avoid 6–7 pm weekday drive if you're coming from Mancheswar — leave by 5:45",
      "Hot oil therapy pairs nicely with a late-evening booking",
      "Saturday afternoons are the busiest at the spa — book two days ahead",
    ],
    services: ["swedish-massage", "aroma-massage", "hot-oil-therapy", "signature"],
  },
);

export const TOPIC_PAGES = {
  "spa-near-me-bhubaneswar": {
    title: "Spa near me in Bhubaneswar",
    seoTitle: "Spa Near Me Bhubaneswar | Massage & Wellness",
    description:
      "The simplest way to find a professional spa near you in Bhubaneswar — services, verified prices, areas we cover, and a direct line to our featured partner.",
    hero: "Spa near me in Bhubaneswar",
    intro:
      "If you're searching for a spa near you in Bhubaneswar, start with a few questions: which part of the city are you in, which style of massage suits your day, and how much time you can give yourself. The three answers usually point to one or two sensible options at Zorba International Spa in Chandrasekharpur.",
    sections: [
      {
        h: "Finding the right fit for your area",
        body: [
          "Chandrasekharpur is a short drive from Patia, KIIT, Sailashree Vihar, Niladri Vihar and the wider Infocity belt.",
          "Jaydev Vihar, Nayapalli, Saheed Nagar and Rasulgarh residents can reach the spa in 15–25 minutes depending on traffic.",
          "Travellers near the airport can plan a 60-minute reset between a flight and the rest of the day.",
        ],
      },
      {
        h: "The questions worth asking before you book",
        body: [
          "What style of massage suits what you're arriving with — tension, tiredness, or just a quiet reset?",
          "How long can you give yourself — 60, 90 or 120 minutes?",
          "Is this a solo visit or a shared one in the couples suite?",
        ],
      },
    ],
    related: ["spa-in-bhubaneswar", "massage-in-bhubaneswar", "spa-prices-bhubaneswar"],
  },
  "spa-in-bhubaneswar": {
    title: "Spa in Bhubaneswar",
    seoTitle: "Spa in Bhubaneswar | Massage, Wellness & Relaxation",
    description:
      "Discover professional spa experiences in Bhubaneswar — services, verified prices and a simple way to enquire. Zorba International Spa is our featured partner.",
    hero: "Spa in Bhubaneswar",
    intro:
      "Finding a reliable spa in Bhubaneswar should feel calm long before you walk in. Here's a clear starting point — what to expect, what to ask, and how to make the first enquiry without feeling rushed.",
    sections: [
      {
        h: "What a good spa visit looks like",
        body: [
          "You should know the service, the duration and the exact price before you walk in. Zorba International Spa publishes its full price list in its official menu — that's the benchmark worth comparing other venues against.",
          "The therapist should ask you a few questions before the session — your comfort with pressure, areas you'd like focus on, and any injuries. This isn't just etiquette, it's how safe, effective bodywork begins.",
        ],
      },
      {
        h: "The questions worth asking first",
        body: [
          "What services do you offer and how long is each session?",
          "What is the exact price for the service I'd like?",
          "Is a couples suite available for the day I'm thinking of?",
          "What's the booking and cancellation process?",
        ],
      },
    ],
    related: ["massage-in-bhubaneswar", "spa-services-bhubaneswar", "spa-prices-bhubaneswar"],
  },
  "massage-in-bhubaneswar": {
    title: "Massage in Bhubaneswar",
    seoTitle: "Massage in Bhubaneswar | Styles, Durations, What to Expect",
    description:
      "An honest guide to massage services in Bhubaneswar — Swedish, deep tissue, aroma, Balinese, Thai, Lomi Lomi, hot stone and more, with exact prices from our featured partner.",
    hero: "Massage in Bhubaneswar",
    intro:
      "Massage styles differ more than most first-time guests expect. A quick overview of the common options available in Bhubaneswar — most of them part of the Zorba International Spa menu — with pointers on which style suits which kind of day.",
    sections: [
      {
        h: "Lighter, relaxing styles",
        body: [
          "Swedish and aroma therapy both lean light. If you're new to spa visits or you simply want to slow down, these are easy, forgiving starting points. Zorba's 60-minute Swedish starts at ₹3,000; aroma at ₹2,800.",
        ],
      },
      {
        h: "Firmer, more focused work",
        body: [
          "Deep tissue and sport massage are slower and firmer, aimed at specific pockets of tension — common for desk workers, drivers and anyone who exercises regularly. Zorba's 60-minute deep tissue is ₹3,500; sport massage ₹4,000.",
        ],
      },
      {
        h: "Signature and traditional styles",
        body: [
          "Balinese, Thai, Lomi Lomi, Hot Stone, Candle, Potli and Bamboo bring in stretches, acupressure and body-weight techniques — plus warmth or natural materials in some cases. They feel more active or more sensory than a classic oil massage.",
        ],
      },
    ],
    related: ["spa-services-bhubaneswar", "body-massage-bhubaneswar", "spa-prices-bhubaneswar"],
  },
  "body-massage-bhubaneswar": {
    title: "Body massage in Bhubaneswar",
    seoTitle: "Body Massage in Bhubaneswar | Full Body Services & Prices",
    description:
      "What a full body massage in Bhubaneswar includes, how long it lasts, and the exact prices at our featured spa partner.",
    hero: "Body massage in Bhubaneswar",
    intro:
      "A full body massage in Bhubaneswar is typically a 60 to 120 minute session covering back, legs, arms, shoulders and head. The style of the massage — Swedish, aroma, Balinese or deep tissue — shapes the pressure and the feel.",
    sections: [
      {
        h: "What's covered in a full body session",
        body: [
          "Back, shoulders and neck — most tension sits here, so therapists usually spend extra time in this area.",
          "Legs and arms — flowing strokes across larger muscle groups to help you unwind.",
          "Head and scalp — a gentle closing section that leaves many guests feeling noticeably calmer.",
        ],
      },
      {
        h: "Choosing the right duration",
        body: [
          "60 minutes covers the essentials. 90 minutes allows a slower pace. 120 minutes is the full, immersive experience — most of Zorba's premium therapies offer a 120-minute option.",
        ],
      },
    ],
    related: ["spa-services-bhubaneswar", "massage-in-bhubaneswar", "spa-prices-bhubaneswar"],
  },
  "wellness-spa-bhubaneswar": {
    title: "Wellness spa in Bhubaneswar",
    seoTitle: "Wellness Spa in Bhubaneswar | Signature Rituals & Jacuzzi",
    description:
      "A wellness spa visit at Zorba International Spa may combine a signature therapy, jacuzzi, body scrub or couples ritual. Here's how to plan it.",
    hero: "Wellness spa in Bhubaneswar",
    intro:
      "A full wellness visit often combines a massage with warm water, a body scrub or a couple's suite ritual. Zorba's signature 6-in-1 experience is the most structured version of this idea — six considered rituals in a single 90-minute flow.",
    sections: [
      {
        h: "The Zorba signature journey",
        body: [
          "90 minutes, layered across a 10-min foot massage, 10-min aroma massage, 20-min deep tissue, 20-min Thai massage, 15-min head massage and 15-min body scrub. ₹6,000 at the time of writing.",
        ],
      },
      {
        h: "Combining massage with jacuzzi or body scrub",
        body: [
          "A short jacuzzi before a massage helps the body relax faster.",
          "A body scrub pairs well with hot oil therapy or Swedish therapy — the exfoliation lets the oil absorb more deeply.",
        ],
      },
    ],
    related: ["jacuzzi-spa-bhubaneswar", "luxury-spa-bhubaneswar", "couples-spa-bhubaneswar"],
  },
  "spa-services-bhubaneswar": {
    title: "Spa services in Bhubaneswar",
    seoTitle: "Spa Services in Bhubaneswar | Full Menu & Prices",
    description:
      "Browse the full spa and massage menu available in Bhubaneswar — including the Zorba Signature Therapy, Four Hands, couples and 15 other services with exact prices.",
    hero: "Spa services in Bhubaneswar",
    intro:
      "An overview of every service on our featured partner's menu. Each has its own feel, pressure and ideal duration. Prices are published transparently — the amount you see is the amount you pay.",
    sections: null,
    related: ["spa-packages-bhubaneswar", "spa-prices-bhubaneswar", "couples-spa-bhubaneswar"],
  },
  "spa-packages-bhubaneswar": {
    title: "Spa packages in Bhubaneswar",
    seoTitle: "Spa Packages in Bhubaneswar | Signature 6-in-1 Experience",
    description:
      "Zorba's signature 6-in-1 wellness experience and couples packages — exact inclusions, duration and transparent prices.",
    hero: "Spa packages in Bhubaneswar",
    intro:
      "Our featured partner's headline package is the Zorba Signature Therapy — a 90-minute 6-in-1 wellness experience at ₹6,000. Couples massage packages span 60 to 120 minutes and include a choice of styles for each partner.",
    sections: [
      {
        h: "Zorba Signature Therapy — 90 min, ₹6,000",
        body: [
          "10-min foot massage, 10-min aroma massage, 20-min deep tissue, 20-min Thai massage, 15-min head massage and 15-min body scrub — in a single, flowing 90-minute journey.",
        ],
      },
      {
        h: "Couple Massage — 60, 90 or 120 minutes",
        body: [
          "₹6,500 for 60 minutes, ₹7,500 for 90 minutes, ₹12,500 for 120 minutes. Each partner can pick any of five styles — Aroma, Swedish, Balinese, Deep Tissue or Candle.",
        ],
      },
      {
        h: "How to compare any spa package fairly",
        body: [
          "Compare on duration and inclusions — not just price.",
          "Ask what the main service style is: a 'full body package' can mean quite different things from spa to spa.",
          "Confirm what's included in the quoted price.",
        ],
      },
    ],
    related: ["spa-prices-bhubaneswar", "spa-offers-bhubaneswar", "luxury-spa-bhubaneswar"],
  },
  "spa-prices-bhubaneswar": {
    title: "Spa prices in Bhubaneswar",
    seoTitle: "Spa Prices in Bhubaneswar | Transparent Price List",
    description:
      "The full published price list at our featured spa partner in Bhubaneswar — every service, every duration, no inflated numbers.",
    hero: "Spa prices in Bhubaneswar",
    intro:
      "Zorba International Spa publishes exact prices for every service across 60, 90 and 120-minute options. The headline range starts at ₹2,500 for a 60-minute Thai massage and tops out at ₹12,500 for a 120-minute couple's experience.",
    sections: [
      {
        h: "Highlights from the price list",
        body: [
          "Thai Massage — ₹2,500 (60 min) / ₹3,400 (90 min) / ₹4,300 (120 min).",
          "Aroma Therapy — ₹2,800 / ₹3,600 / ₹4,500.",
          "Swedish Therapy — ₹3,000 / ₹4,000 / ₹4,900.",
          "Deep Tissue Massage — ₹3,500 / ₹4,500 / ₹5,500.",
          "Zorba Signature Therapy — ₹6,000 for 90 minutes.",
          "Couple Massage — ₹6,500 / ₹7,500 / ₹12,500.",
        ],
      },
      {
        h: "Questions worth asking",
        body: [
          "Does the quoted price include taxes?",
          "Are there any add-ons that would affect the final amount?",
          "What's the cancellation and rescheduling policy?",
        ],
      },
    ],
    related: ["spa-packages-bhubaneswar", "spa-offers-bhubaneswar", "spa-booking-bhubaneswar"],
  },
  "spa-offers-bhubaneswar": {
    title: "Spa offers in Bhubaneswar",
    seoTitle: "Spa Offers in Bhubaneswar | Honest Guide",
    description:
      "A transparent view of spa offers in Bhubaneswar — what to look for and what to avoid.",
    hero: "Spa offers in Bhubaneswar",
    intro:
      "Spa offers can be useful — but only when they're transparent. We only publish promotions when our partner spa confirms them, and never with inflated 'original' prices.",
    sections: [
      {
        h: "Good signs in a spa offer",
        body: [
          "The inclusions are clearly listed.",
          "The duration is specified.",
          "The valid dates are stated.",
          "The cancellation policy is simple to understand.",
        ],
      },
      {
        h: "What to watch out for",
        body: [
          "Discounts stated against an inflated 'original' price",
          "Offers that require long memberships upfront",
          "Vague inclusions ('all services covered')",
        ],
      },
    ],
    related: ["spa-packages-bhubaneswar", "spa-prices-bhubaneswar", "spa-booking-bhubaneswar"],
  },
  "spa-booking-bhubaneswar": {
    title: "Spa booking in Bhubaneswar",
    seoTitle: "Spa Booking in Bhubaneswar | Simple, Transparent Process",
    description:
      "How to book a spa or massage appointment in Bhubaneswar — the simple, respectful way.",
    hero: "Spa booking in Bhubaneswar",
    intro:
      "Booking a spa appointment shouldn't be complicated. Share a few details, confirm with the spa, and arrive a few minutes early. Here's how we think about it.",
    sections: [
      {
        h: "A simple booking flow",
        body: [
          "Start with a short enquiry — service, approximate time, number of guests.",
          "Our featured partner, Zorba International Spa, confirms availability over WhatsApp or phone.",
          "You arrive 10 minutes before your slot to settle in.",
        ],
      },
      {
        h: "What to prepare",
        body: [
          "Your preferred service and duration",
          "Any health notes — pregnancy, injuries, allergies, strong scent sensitivities",
          "A rough preferred time window",
        ],
      },
    ],
    related: ["spa-packages-bhubaneswar", "spa-prices-bhubaneswar", "zorba-international-spa"],
  },
  "couples-spa-bhubaneswar": {
    title: "Couples spa in Bhubaneswar",
    seoTitle: "Couples Spa in Bhubaneswar | Shared Massage Experiences",
    description:
      "A thoughtful look at couples spa experiences in Bhubaneswar — exact prices, inclusions and how to plan a visit.",
    hero: "Couples spa in Bhubaneswar",
    intro:
      "A couples spa visit is more than two massages scheduled in parallel. It's a shared pause — a private candle-lit room, two therapists, and time that belongs only to the two of you. Zorba's couples suite is purpose-built for exactly this.",
    sections: [
      {
        h: "What a couples session includes at Zorba",
        body: [
          "A private candle-lit couples suite with two side-by-side treatment tables.",
          "Two therapists working in sync.",
          "A choice of five styles for each partner: Aroma, Swedish, Balinese, Deep Tissue or Candle.",
          "Durations: 60 min (₹6,500), 90 min (₹7,500) or 120 min (₹12,500).",
        ],
      },
      {
        h: "Planning the visit",
        body: [
          "Confirm couples-suite availability for your date and time.",
          "Think about what you'd like to do afterwards — dinner, a quiet drive, or simply going home relaxed.",
        ],
      },
    ],
    related: ["luxury-spa-bhubaneswar", "jacuzzi-spa-bhubaneswar", "spa-packages-bhubaneswar"],
  },
  "jacuzzi-spa-bhubaneswar": {
    title: "Jacuzzi spa in Bhubaneswar",
    seoTitle: "Jacuzzi Spa in Bhubaneswar | Warm Water Wellness",
    description:
      "A practical guide to jacuzzi therapy at Zorba International Spa — exact prices, how to combine with a massage and what to expect.",
    hero: "Jacuzzi spa in Bhubaneswar",
    intro:
      "A jacuzzi is a warm, moving-water soak that pairs beautifully with a massage. Zorba's jacuzzi therapy is published as a standalone experience at ₹5,000 for 60 minutes, ₹6,000 for 90 minutes and ₹7,000 for 120 minutes.",
    sections: [
      {
        h: "How to combine jacuzzi with massage",
        body: [
          "A short soak before a massage helps the body relax faster.",
          "A short jacuzzi afterwards can extend the calm, but keep it brief.",
        ],
      },
      {
        h: "A few things to keep in mind",
        body: [
          "Avoid alcohol beforehand.",
          "Hydrate well.",
          "Allow time to cool down before leaving.",
        ],
      },
    ],
    related: ["luxury-spa-bhubaneswar", "wellness-spa-bhubaneswar", "spa-packages-bhubaneswar"],
  },
  "luxury-spa-bhubaneswar": {
    title: "Luxury spa in Bhubaneswar",
    seoTitle: "Luxury Spa in Bhubaneswar | A Premium Wellness Experience",
    description:
      "What a premium spa experience in Bhubaneswar can look like — considered rooms, trained therapists and a published, transparent price list.",
    hero: "Luxury spa in Bhubaneswar",
    intro:
      "'Luxury' is less about marble and gold and more about care. A premium spa experience in Bhubaneswar is defined by the therapist's training, the cleanliness of the space, and the clarity of information before and after your visit. Zorba's tagline — 'a world of wellness wrapped in luxury' — points at exactly this.",
    sections: [
      {
        h: "What makes a premium experience",
        body: [
          "Clean, quiet, well-maintained treatment rooms.",
          "Trained therapists who adjust pressure and pace carefully.",
          "A clear service list, transparent pricing on request, and no sales pressure.",
        ],
      },
      {
        h: "Signs of a thoughtful spa",
        body: [
          "They answer questions patiently over WhatsApp.",
          "They explain what's included in each service.",
          "They respect your preferences — pressure, scent, music, lighting.",
        ],
      },
    ],
    related: ["couples-spa-bhubaneswar", "jacuzzi-spa-bhubaneswar", "spa-packages-bhubaneswar"],
  },
};

export const TOPIC_SLUGS = Object.keys(TOPIC_PAGES);

export const HOME_FAQ = [
  {
    q: "What should I expect during my first spa visit in Bhubaneswar?",
    a: "A short consultation to understand your preferences, a quiet, private treatment room, and a session that stays within your comfort limits. Share any sensitivities with your therapist beforehand.",
  },
  {
    q: "Are prices listed on this website?",
    a: "Yes. Our featured partner Zorba International Spa publishes an exact price list — from ₹2,500 for a 60-minute Thai massage to ₹12,500 for a 120-minute couple's experience.",
  },
  {
    q: "Is this an official directory of spas in Bhubaneswar?",
    a: "No. Spa Near Me Bhubaneswar is a discovery and enquiry platform. Zorba International Spa is presented transparently as our featured spa partner.",
  },
  {
    q: "How do I book a spa appointment?",
    a: "Send a short enquiry with your preferred service and time, or WhatsApp Zorba directly. The spa will confirm availability before you arrive.",
  },
  {
    q: "Can I book a couples spa in Bhubaneswar?",
    a: "Yes. Zorba's couples suite seats two therapists side-by-side; each partner can pick a different style from Aroma, Swedish, Balinese, Deep Tissue or Candle.",
  },
];

export const GUIDES = [
  { slug: "choose-spa-bhubaneswar", title: "How to choose a spa in Bhubaneswar: a calm checklist", read: "5 min" },
  { slug: "massage-price-guide", title: "Massage price guide for Bhubaneswar: what to ask before booking", read: "6 min" },
  { slug: "swedish-vs-deep-tissue", title: "Swedish vs deep tissue: which style suits you?", read: "4 min" },
  { slug: "first-spa-visit", title: "What to expect during your first spa visit", read: "5 min" },
  { slug: "spa-etiquette-india", title: "Spa etiquette in India: a short, friendly primer", read: "4 min" },
  { slug: "couples-spa-planning", title: "Planning a couples spa visit in Bhubaneswar", read: "5 min" },
  { slug: "spa-near-airport", title: "Spa near Bhubaneswar Airport: a traveller's guide", read: "4 min" },
  { slug: "massage-after-travel", title: "Massage after travel: how to pick the right session", read: "4 min" },
  { slug: "massage-after-workday", title: "Massage after a long workday: a 60-minute reset", read: "4 min" },
  { slug: "massage-session-length", title: "How long should a massage session be?", read: "3 min" },
  { slug: "what-to-wear", title: "What should you wear to a massage?", read: "3 min" },
  { slug: "spa-near-kiit", title: "Spa near KIIT and Patia: a local overview", read: "5 min" },
];

export const ANALYTICS_EVENTS = {
  whatsappClick: "whatsapp_click",
  callClick: "call_click",
  bookOpen: "book_open",
  bookSubmit: "booking_form_submit",
  bookSaved: "enquiry_saved",
  directionsClick: "directions_click",
  serviceView: "service_view",
  areaView: "area_view",
};
